import React, { useEffect, useState } from 'react';
import { useLoadScript, GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MapPin, MagnifyingGlass, NavigationArrow } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const containerStyle = { width: '100%', height: '400px', borderRadius: '12px' };
const defaultCenter = { lat: 5.4164, lng: 100.3327 };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-scoped promises → survive React StrictMode double-mount without duplicating fetches
let _mapsKeyPromise = null;
const fetchMapsKey = () => {
  if (!_mapsKeyPromise) {
    _mapsKeyPromise = fetch(`${API}/settings/public`).then(r => r.json()).then(s => s?.google_maps_api_key || '').catch(() => '');
  }
  return _mapsKeyPromise;
};

let _publicDataPromise = null;
const fetchPublicData = () => {
  if (!_publicDataPromise) {
    _publicDataPromise = Promise.all([
      fetch(`${API}/pharmacies/public`).then(r => r.json()).catch(() => []),
      fetch(`${API}/products/public`).then(r => r.json()).catch(() => []),
      fetch(`${API}/partners/public`).then(r => r.json()).catch(() => []),
    ]).then(([pharms, prods, partnrs]) => ({
      pharms: Array.isArray(pharms) ? pharms : [],
      prods: Array.isArray(prods) ? prods : [],
      partnrs: Array.isArray(partnrs) ? partnrs : [],
    }));
  }
  return _publicDataPromise;
};

/**
 * MapWithKey — the ONLY component that calls useLoadScript.
 * It is mounted exclusively when a non-empty apiKey is available, so
 * @react-google-maps/api never injects a keyless <script> tag.
 */
const MapWithKey = ({ apiKey, center, markers, onMarkerClick, selectedPharmacy, onCloseInfo }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    id: 'octoboots-gmaps',
    preventGoogleFontsLoading: true,
  });
  if (!isLoaded) return <div className="p-4 text-sm text-slate-500">Loading map…</div>;
  return (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={12}>
      {markers.map(p => (
        <MarkerF key={p.id} position={{ lat: p.latitude, lng: p.longitude }} onClick={() => onMarkerClick(p)} />
      ))}
      {selectedPharmacy && (
        <InfoWindowF position={{ lat: selectedPharmacy.latitude, lng: selectedPharmacy.longitude }} onCloseClick={onCloseInfo}>
          <div><strong>{selectedPharmacy.name}</strong><br />{selectedPharmacy.address}</div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
};

export const PublicMapPage = () => {
  const { language, changeLanguage } = useLanguage();
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [mapsApiKey, setMapsApiKey] = useState('');
  const [keyLoaded, setKeyLoaded] = useState(false);

  useEffect(() => {
    fetchMapsKey().then(k => { setMapsApiKey(k); setKeyLoaded(true); });
    fetchPublicData().then(({ pharms, prods, partnrs }) => {
      setPharmacies(pharms);
      setProducts(prods);
      setPartners(partnrs);
    });
  }, []);

  const useMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => alert('Location access denied')
      );
    }
  };

  const distance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lat2) return Infinity;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  let filtered = pharmacies.filter(p =>
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.area?.toLowerCase().includes(search.toLowerCase()) || p.address?.toLowerCase().includes(search.toLowerCase()))
  );
  if (userLocation) {
    filtered = filtered.map(p => ({ ...p, distance: distance(userLocation.lat, userLocation.lng, p.latitude, p.longitude) })).sort((a, b) => a.distance - b.distance);
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto p-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Where to Buy</h1>
          <button onClick={() => changeLanguage(language === 'en' ? 'zh' : 'en')} className="text-xs px-2 py-1 rounded border border-slate-300">
            {language === 'en' ? '中文' : 'EN'}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <Card className="p-4 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-input" placeholder="Search by name, area, or address..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger data-testid="product-filter"><SelectValue placeholder="All Products" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger data-testid="partner-filter"><SelectValue placeholder="All Brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="use-location-btn" variant="outline" size="sm" onClick={useMyLocation} className="w-full">
            <NavigationArrow size={16} className="mr-2" /> Use My Location (nearest first)
          </Button>
        </Card>

        {mapsApiKey ? (
          <Card className="p-2 overflow-hidden">
            <MapWithKey
              apiKey={mapsApiKey}
              center={userLocation || defaultCenter}
              markers={filtered.filter(p => p.latitude)}
              onMarkerClick={setSelectedPharmacy}
              selectedPharmacy={selectedPharmacy}
              onCloseInfo={() => setSelectedPharmacy(null)}
            />
          </Card>
        ) : (
          <Card className="p-4 bg-yellow-50 text-yellow-800 text-sm">
            {keyLoaded ? 'Google Maps API key not configured. Admin can set it in Settings.' : 'Loading…'}
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((pharm) => (
            <Card key={pharm.id} data-testid={`public-pharm-${pharm.id}`} className="p-4">
              {pharm.storefront_photo && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/pharmacies/storefront-image/${pharm.id}`} alt={pharm.name} className="w-full h-32 object-cover rounded-lg mb-3" />
              )}
              <div className="flex items-start gap-3">
                <MapPin size={28} className="text-blue-700 shrink-0" weight="fill" />
                <div className="flex-1">
                  <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>{pharm.name}</h3>
                  {pharm.branch_name && <p className="text-sm text-slate-600">{pharm.branch_name}</p>}
                  <p className="text-sm text-slate-700 mt-1">{pharm.address}</p>
                  <p className="text-xs text-slate-500 mt-1">{pharm.area} {pharm.distance && `• ${pharm.distance.toFixed(1)} km`}</p>
                  {pharm.latitude && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${pharm.latitude},${pharm.longitude}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-blue-700 hover:underline">
                      Get Directions →
                    </a>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p className="col-span-2 text-center py-8 text-slate-500">No pharmacies found</p>}
        </div>
      </div>
    </div>
  );
};
